import React, { useEffect, useState, useCallback } from 'react';
import { Database, AlertCircle, Plus, Trash2, Pencil } from 'lucide-react';
import { fetchDemoRecords, addDemoRecord, updateDemoRecord, removeDemoRecord, subscribeDemoCollection } from '../services/frontendDemoAPI';

export default function Pocketbase() {
    const [records, setRecords] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pendingAdd, setPendingAdd] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState(null); // id of updating record
    const [pendingRemove, setPendingRemove] = useState(null); // id of removing record

    const refreshRecords = useCallback(() => {
        setInitialLoading(true);
        setError(null);
        fetchDemoRecords()
            .then((data) => {
                setRecords(data);
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => setInitialLoading(false));
    }, []);

    useEffect(() => {
        refreshRecords();
        // Subscribe to real-time updates (cross-device)
        const unsubscribe = subscribeDemoCollection((newRecords) => {
            setRecords(newRecords);
        });
        return () => unsubscribe();
    }, [refreshRecords]);

    const handleAdd = useCallback(async () => {
        setPendingAdd(true);
        setError(null);
        try {
            await addDemoRecord({ title: 'New Record', description: 'Demo description' });
            // Do NOT setRecords here. Wait for Socket.IO to update the state!
        } catch (err) {
            setError(err.message);
        } finally {
            setPendingAdd(false);
        }
    }, []);

    const handleRemove = useCallback(async (id) => {
        setPendingRemove(id);
        setError(null);
        try {
            await removeDemoRecord(id);
            setRecords((prev) => prev.filter((r) => r.id !== id));
        } catch (err) {
            setError(err.message);
        } finally {
            setPendingRemove(null);
        }
    }, []);

    const handleUpdate = useCallback(async (id) => {
        setPendingUpdate(id);
        setError(null);
        try {
            const updated = await updateDemoRecord(id, { title: 'Updated Title', description: 'Updated description' });
            setRecords((prev) => prev.map((r) => r.id === id ? updated : r));
        } catch (err) {
            setError(err.message);
        } finally {
            setPendingUpdate(null);
        }
    }, []);

    if (initialLoading) return (
        <div className="flex flex-col items-center justify-center h-40 text-gray-500">
            <Database className="w-8 h-8 mb-2" />
            <span>Loading…</span>
        </div>
    );

    if (error) return (
        <div className="flex flex-col items-center justify-center h-40 text-red-600">
            <AlertCircle className="w-8 h-8 mb-2" />
            <span className="text-sm">Error: {error}</span>
        </div>
    );

    return (
        <div className="max-w-md mx-auto p-4">
            <h1 className="flex items-center gap-2 text-lg font-bold mb-4">
                <Database className="w-6 h-6" />
                Pocketbase Records
            </h1>
            <button
                className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer mb-4 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleAdd}
                disabled={pendingAdd}
            >
                <Plus className="w-4 h-4" />
                {pendingAdd ? 'Adding…' : 'Add Record'}
            </button>
            <ul className="space-y-3">
                {records.map((record) => (
                    <li key={record.id} className="p-4 bg-white rounded-md shadow border border-gray-200 flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <Database className="w-5 h-5 text-green-600" />
                            <span className="font-semibold text-gray-900 text-base">{record.title}</span>
                        </div>
                        <span className="text-gray-700 text-sm">{record.description}</span>
                        <span className="text-xs text-gray-400">ID: {record.id}</span>
                        <div className="flex gap-2 mt-2">
                            <button
                                className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                onClick={() => handleUpdate(record.id)}
                                disabled={pendingUpdate === record.id}
                            >
                                <Pencil className="w-4 h-4" />
                                {pendingUpdate === record.id ? 'Updating…' : 'Update'}
                            </button>
                            <button
                                className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                onClick={() => handleRemove(record.id)}
                                disabled={pendingRemove === record.id}
                            >
                                <Trash2 className="w-4 h-4" />
                                {pendingRemove === record.id ? 'Removing…' : 'Remove'}
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
